// src/api/campaign/campaignApi.ts
import { axiosInstance } from "src/api/axiosInstance";
import type { ApiResponse } from "src/api/common/apiTypes";
import type {
  CampaignListItem,
  CampaignItem,
  CampaignListResponse,
  GetCampaignListParams,
  CampaignCreateBody,
  CampaignUpdateBody,
  CampaignStatistics,
  CampaignParticipationResponse,
  GetCampaignParticipationsParams,
  RewardIssueFailedResponse,
  RewardIssueDetail,
  GetRewardIssuesFailedParams,
  ReissueResponse,
} from "src/types/campaign";

// LIST
export const getCampaignListApi = async (
  params: GetCampaignListParams
): Promise<ApiResponse<CampaignListResponse>> => {
  const res = await axiosInstance.get<ApiResponse<CampaignListResponse>>("/v1/admin/campaigns", {
    params: {
      triggerType: params.triggerType || undefined,
      page: params.page,
      size: params.size,
      sort: params.sort && params.sort.length > 0 ? params.sort : [],
    },
  });
  return res.data;
};

// DETAIL
export const getCampaignApi = async (id: number): Promise<ApiResponse<CampaignItem>> => {
  const res = await axiosInstance.get<ApiResponse<CampaignItem>>(`/v1/admin/campaigns/${id}`);
  return res.data;
};

// CREATE
export const createCampaignApi = async (
  payload: CampaignCreateBody
): Promise<ApiResponse<CampaignItem>> => {
  const res = await axiosInstance.post<ApiResponse<CampaignItem>>("/v1/admin/campaigns", payload);
  return res.data;
};

// UPDATE
export const updateCampaignApi = async (
  id: number,
  payload: CampaignUpdateBody
): Promise<ApiResponse<CampaignItem>> => {
  const res = await axiosInstance.put<ApiResponse<CampaignItem>>(
    `/v1/admin/campaigns/${id}`,
    payload
  );
  return res.data;
};

// DELETE
export const deleteCampaignApi = async (id: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(`/v1/admin/campaigns/${id}`);
  return res.data;
};

// STATUS CHANGE
export const updateCampaignStatusApi = async (
  id: number,
  isActive: boolean
): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.patch<ApiResponse<null>>(`/v1/admin/campaigns/${id}`, {
    isActive,
  });
  return res.data;
};

// STATISTICS (캠페인 통계 조회)
export const getCampaignStatisticsApi = async (
  campaignId: number,
  params?: { startDate?: string; endDate?: string }
): Promise<ApiResponse<CampaignStatistics>> => {
  const res = await axiosInstance.get<ApiResponse<CampaignStatistics>>(
    `/v1/admin/campaigns/${campaignId}/statistics`,
    { params }
  );
  return res.data;
};

// PARTICIPATIONS (캠페인 참여 로그)
export const getCampaignParticipationsApi = async (
  params: GetCampaignParticipationsParams
): Promise<ApiResponse<CampaignParticipationResponse>> => {
  const { campaignId, ...searchCondition } = params;
  const res = await axiosInstance.get<ApiResponse<CampaignParticipationResponse>>(
    `/v1/admin/campaigns/${campaignId}/participations`,
    {
      params: {
        ...searchCondition,
        sort: searchCondition.sort && searchCondition.sort.length > 0 ? searchCondition.sort : [],
      },
      snackbar: { success: false },
    }
  );
  return res.data;
};

// REWARD ISSUANCES (발급 이력 목록 — issueStatus 미지정 시 FAILED/PENDING 조회)
export const getRewardIssuesFailedApi = async (
  params: GetRewardIssuesFailedParams
): Promise<ApiResponse<RewardIssueFailedResponse>> => {
  const res = await axiosInstance.get<ApiResponse<RewardIssueFailedResponse>>(
    "/v1/admin/reward-issuances",
    {
      params: {
        ...params,
        page: params.page,
        size: params.size,
      },
    }
  );
  return res.data;
};

// REWARD ISSUANCE DETAIL (발급 이력 상세)
export const getRewardIssuanceApi = async (
  issueId: number
): Promise<ApiResponse<RewardIssueDetail>> => {
  const res = await axiosInstance.get<ApiResponse<RewardIssueDetail>>(
    `/v1/admin/reward-issuances/${issueId}`,
    { snackbar: { success: false } }
  );
  return res.data;
};

// RETRY (수동 재발급 — 원본 보상 세트 전체 재발급만 지원)
export const reissueRewardApi = async (issueId: number): Promise<ApiResponse<ReissueResponse>> => {
  const res = await axiosInstance.post<ApiResponse<ReissueResponse>>(
    `/v1/admin/reward-issuances/${issueId}/retries`,
    {}
  );
  return res.data;
};

export type { CampaignListItem, CampaignItem };
